import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearPlayersCache } from '../services/playerService';
import { listChats, createChat, getChatMessages, streamChatMessage, ChatMessage, ChatListItem } from '../api/chat';
import { getAuthToken } from '../utils/auth';

export default function useChat(initialLeagueId?: string, initialPlayerName?: string) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [chats, setChats] = useState<ChatListItem[]>([]);
  const [currentThought, setCurrentThought] = useState<string | null>(null);

  // initialize: token check, load or create chat, load messages
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const listParams = initialLeagueId || initialPlayerName 
          ? { leagueId: initialLeagueId, playerName: initialPlayerName }
          : undefined;
        const list = await listChats(listParams);
        if (list.length) {
          const sorted = list.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          setChats(sorted);
          setChatId(sorted[0].chatId);
        } else {
          const createParams = initialLeagueId || initialPlayerName
            ? { leagueId: initialLeagueId, playerName: initialPlayerName }
            : undefined;
          const created = await createChat(createParams);
          setChatId(created.chatId);
        }
      } catch (e) {
        console.error('Failed to init chats', e);
      }
    })();
  }, [navigate, initialLeagueId, initialPlayerName]);

  useEffect(() => {
    if (!chatId) return;
    (async () => {
      try {
        const msgs = await getChatMessages(chatId);
        setMessages(msgs);
      } catch (e) {
        console.error('Failed to get messages', e);
      }
    })();
  }, [chatId]);

  const handleLogout = () => {
    localStorage.removeItem('google_token');
    localStorage.removeItem('user');
    clearPlayersCache();
    navigate('/');
  };

  const sendMessage = async (text: string) => {
    if (!chatId || !text.trim()) return;
    const userMsg: ChatMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    setCurrentThought(null); // Start with null, not '...'

    try {
      const reader = await streamChatMessage(chatId, text);
      let assistantContent = '';
      let thinkingContent = '';
      let done = false;
      let buffer = '';

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          const chunk = new TextDecoder().decode(value);
          buffer += chunk;
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const parsed = JSON.parse(line);
              if (parsed.type === 'thinking' && typeof parsed.content === 'string') {
                thinkingContent = parsed.newThought ? parsed.content : (thinkingContent + parsed.content);
                setCurrentThought(thinkingContent || null);
              } else if (parsed.type === 'final') {
                setCurrentThought(null);
                thinkingContent = '';
                assistantContent += parsed.content || '';
                
                // Update or create assistant message
                setMessages(prev => {
                  const last = prev[prev.length - 1];
                  if (last && last.role === 'assistant') {
                    return [...prev.slice(0, -1), { role: 'assistant', content: assistantContent }];
                  }
                  return [...prev, { role: 'assistant', content: assistantContent }];
                });
              }
            } catch (e) {
              console.error('parse error', e);
            }
          }
        }
      }
    } catch (err: any) {
      if (err?.code === 401) {
        localStorage.setItem('pending_message', text);
        setMessages(prev => prev.slice(0, -1));
      } else if (err?.code === 403) {
        alert('User not allowed');
        handleLogout();
      } else {
        console.error(err);
        setMessages(prev => [...prev, { role: 'assistant', content: 'Error: Could not get response.' }]);
      }
    } finally {
      setLoading(false);
      setCurrentThought(null);
    }
  };

  const selectChat = async (id: string) => {
    if (id === chatId) return;
    setChatId(id);
    setMessages([]);
  };

  const newChat = async (leagueId?: string, playerName?: string) => {
    setMessages([]);
    setChatId(null);
    const params = leagueId || playerName
      ? { leagueId, playerName }
      : undefined;
    const created = await createChat(params);
    setChatId(created.chatId);
  };

  return {
    messages,
    sendMessage,
    loading,
    currentThought,
    chats,
    selectChat,
    newChat,
    chatId,
  };
}
